/* oxlint-disable no-void */
import type React from 'react';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { di } from 'react-magnetic-di';
import { useLatestRef } from 'view/common/use-latest-ref';
import { useInlineField } from 'view/common/forge-custom-fields/controllers/store';
import {
	FIELD_STATE_FOCUSED,
	FIELD_STATE_PENDING,
	FIELD_STATE_STORED,
	FIELD_STATE_IDLE,
	FIELD_STATE_FAILED,
} from 'view/common/forge-custom-fields/controllers/store/constants';
import {
	fireOperationalEditFailedEvent,
	fireTrackEditedEvent,
} from 'view/forge-ui-analytics/services/custom-field-edit';
import type { ForgeCustomFieldAnalyticsAttributes } from 'view/common/forge-custom-fields/types';

export interface Props {
	fieldId: string;
	containerRef: React.RefObject<HTMLDivElement | null>;
	onSubmit: (fieldValue: unknown) => void;
	analyticsAttributes: ForgeCustomFieldAnalyticsAttributes;
}

export const useForgeCustomFieldStateSync = ({
	fieldId,
	containerRef,
	onSubmit,
	analyticsAttributes,
}: Props) => {
	di(useInlineField, fireOperationalEditFailedEvent, fireTrackEditedEvent);

	const [fieldState, { setFieldState }] = useInlineField(fieldId);
	const fieldStateRef = useRef(fieldState);
	const recentSubmitIdRef = useRef(Symbol('submit-id'));

	const isInFocus = useCallback(() => {
		return (
			// Upgrading @atlaskit/platform; fix these violations.
			// eslint-disable-next-line @atlaskit/platform/no-direct-document-usage
			containerRef.current === document.activeElement ||
			// eslint-disable-next-line @atlaskit/platform/no-direct-document-usage
			containerRef.current?.contains(document.activeElement)
		);
	}, [containerRef]);

	const syncFieldFocusState = useCallback(() => {
		if (isInFocus()) {
			void setFieldState({
				fieldId,
				status: FIELD_STATE_FOCUSED,
			});
		} else if (fieldStateRef.current?.status === FIELD_STATE_FOCUSED) {
			void setFieldState({
				fieldId,
				status: FIELD_STATE_PENDING,
			});
		}
	}, [isInFocus, setFieldState, fieldId]);

	/**
	 * This function is called when the form is submitted.
	 * It is used to sync the actual state of the field with our internal storage.
	 * This is necessary because the Forge fields defined using Custom UI are rendered in the iframe and not all their focus/blur events are propagated to the parent.
	 * We also want to manually trigger the blur event on the field so attached blur handlers kick in and the field's value gets submitted.
	 */
	const onFormSubmit = useCallback(() => {
		syncFieldFocusState();
		if (isInFocus()) {
			// Upgrading @atlaskit/platform; fix these violations.
			// eslint-disable-next-line @atlaskit/platform/no-direct-document-usage
			(document.activeElement as HTMLElement)?.blur?.();
		}
	}, [syncFieldFocusState, isInFocus]);

	useEffect(
		() => {
			void setFieldState({
				onFormSubmit,
				fieldId,
				status: FIELD_STATE_IDLE,
			});
		},
		// Keep these dependencies immutable, otherwise we are going to override the field's state.
		[fieldId, onFormSubmit, setFieldState],
	);

	useEffect(() => {
		fieldStateRef.current = fieldState;
	}, [fieldState, setFieldState]);

	useEffect(() => {
		syncFieldFocusState();

		// Wait for the focus to be moved to the element.
		// In case of iframes it is not immediate so we must use a slight delay.
		const delayedFieldFocusStateSync = () => setTimeout(syncFieldFocusState, 100);
		// Upgrading @atlaskit/platform; fix these violations.
		// eslint-disable-next-line @atlaskit/platform/no-direct-document-usage
		document.addEventListener('focusout', delayedFieldFocusStateSync);

		return () => {
			// Upgrading @atlaskit/platform; fix these violations.
			// eslint-disable-next-line @atlaskit/platform/no-direct-document-usage
			document.removeEventListener('focusout', delayedFieldFocusStateSync);
		};
	}, [syncFieldFocusState, fieldId]);

	/**
	 * Submit bridge method implementation.
	 * It is possible for the app to programmatically submit the field multiple times.
	 * And it may be the case that they submit a promise that takes time to resolve.
	 * Because of that we need to update the state when the last pending submit is resolved.
	 */
	const analyticsAttributesRef = useLatestRef(analyticsAttributes);

	const submit = useCallback(
		async ({ data }: { data: unknown }) => {
			recentSubmitIdRef.current = Symbol('submit-id');
			const scopedSubmitId = recentSubmitIdRef.current;
			await setFieldState({
				fieldId,
				status: FIELD_STATE_PENDING,
			});

			try {
				const resolvedValue = await Promise.resolve(data);
				if (scopedSubmitId === recentSubmitIdRef.current) {
					onSubmit(resolvedValue);
					await setFieldState({
						fieldId,
						status: FIELD_STATE_STORED,
					});
					const { source, renderContext, experience } = analyticsAttributesRef.current;
					await fireTrackEditedEvent(source, {
						renderContext,
						experience,
					});
				}
			} catch (error) {
				await setFieldState({
					fieldId,
					status: FIELD_STATE_FAILED,
				});
				const { source, renderContext, experience } = analyticsAttributesRef.current;
				await fireOperationalEditFailedEvent(source, {
					renderContext,
					experience,
					error: error as Error,
				});
			}

			return undefined;
		},
		[fieldId, setFieldState, onSubmit, analyticsAttributesRef],
	);

	return useMemo(() => ({ submit }), [submit]);
};
